import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import { useImportContacts, suggestMapping, ImportedContact } from '../../../hooks/useImportContacts';
import { notify } from '../../../utils/toast';
import { ImportStep, ImportError, ImportStats } from './ImportWizard/types';
import { UploadStep } from './ImportWizard/steps/UploadStep';
import { MappingStep } from './ImportWizard/steps/MappingStep';
import { ImportProgressStep } from './ImportWizard/steps/ImportProgressStep';
import { ResultStep } from './ImportWizard/steps/ResultStep';

interface ImportContactsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImportFinished: (count: number) => void;
    createContactService: (contact: ImportedContact) => Promise<any>;
}

const ImportContactsModal: React.FC<ImportContactsModalProps> = ({
    isOpen, onClose, onImportFinished, createContactService
}) => {
    const { isParsing, parseFile, processContacts } = useImportContacts();

    // Estados do Wizard e Dados
    const [step, setStep] = useState<ImportStep>('UPLOAD');
    const [headerList, setHeaderList] = useState<string[]>([]);
    const [rawData, setRawData] = useState<Record<string, any>[]>([]);
    const [mapping, setMapping] = useState<Record<string, string>>({});

    // Estados de Processamento
    const [progress, setProgress] = useState(0);
    const [stats, setStats] = useState<ImportStats>({ total: 0, success: 0, error: 0 });
    const [errors, setErrors] = useState<ImportError[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);

    const reset = () => {
        setStep('UPLOAD');
        setHeaderList([]);
        setRawData([]);
        setMapping({});
        setProgress(0);
        setStats({ total: 0, success: 0, error: 0 });
        setErrors([]);
    };

    const handleClose = () => {
        if (isProcessing && !confirm('A importação está em andamento. Deseja cancelar?')) return;
        reset();
        onClose();
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const { data, headers } = await parseFile(file);
            setRawData(data);
            setHeaderList(headers);
            setMapping(suggestMapping(headers));
            setStep('MAPPING');
        } catch (error: any) {
            notify.error(error.message || 'Erro ao ler arquivo');
        }
    };

    const handleStartImport = async () => {
        setIsProcessing(true);
        setStep('IMPORTING');
        setErrors([]);

        try {
            const contactsToImport = processContacts(rawData, mapping);
            if (contactsToImport.length === 0) {
                notify.error('Nenhum contato válido encontrado com mapeamento atual.');
                setStep('MAPPING');
                setIsProcessing(false);
                return;
            }

            setStats({ total: contactsToImport.length, success: 0, error: 0 });

            let successCount = 0;
            let errorCount = 0;
            const currentErrors: ImportError[] = [];

            for (let i = 0; i < contactsToImport.length; i++) {
                try {
                    await createContactService(contactsToImport[i]);
                    successCount++;
                } catch (error: any) {
                    errorCount++;
                    const lowerCode = error.code || '';
                    const lowerMsg = (error.message || '').toLowerCase();
                    let reason = error.message || 'Erro desconhecido';

                    if (lowerCode === '23505' || lowerMsg.includes('duplicate') || lowerMsg.includes('already exists')) {
                        reason = 'Duplicado (já existe)';
                    } else if (lowerCode === '42501') {
                        reason = 'Permissão negada (RLS)';
                    } else if (lowerCode === '22001') {
                        reason = 'Dado muito longo';
                    }

                    // SEGURANÇA: Não logamos dados brutos (PII) no console
                    currentErrors.push({
                        row: i + 2,
                        name: contactsToImport[i].name || '(Sem Nome)',
                        reason
                    });
                }

                setProgress(Math.round(((i + 1) / contactsToImport.length) * 100));
                setStats(prev => ({ ...prev, success: successCount, error: errorCount }));

                if (i % 20 === 0) await new Promise(r => setTimeout(r, 0));
            }

            setErrors(currentErrors);
            setStep('RESULT');
            onImportFinished(successCount);
        } catch (error) {
            console.error('Erro geral na importação', error);
            notify.error('Ocorreu um erro durante o processo de importação.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={step === 'MAPPING' ? 'lg' : 'md'}>
            <Modal.Header onClose={handleClose}>
                {step === 'UPLOAD' && 'Importar Contatos'}
                {step === 'MAPPING' && 'Mapear Colunas'}
                {step === 'IMPORTING' && 'Importando...'}
                {step === 'RESULT' && 'Resumo da Importação'}
            </Modal.Header>
            <Modal.Body>
                {step === 'UPLOAD' && (
                    <UploadStep onFileSelect={handleFileSelect} isParsing={isParsing} />
                )}
                {step === 'MAPPING' && (
                    <MappingStep
                        headerList={headerList}
                        rawData={rawData}
                        mapping={mapping}
                        onMappingChange={(header, field) => setMapping(prev => ({ ...prev, [header]: field }))}
                        onBack={reset}
                        onNext={handleStartImport}
                    />
                )}
                {step === 'IMPORTING' && (
                    <ImportProgressStep progress={progress} stats={stats} />
                )}
                {step === 'RESULT' && (
                    <ResultStep stats={stats} errors={errors} onClose={handleClose} />
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ImportContactsModal;
